import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Table } from '../../../ui-library';
import ClientsPagination from './ClientsPagination';

type Paging = {
    pageSize: number;
    totalCount: number;
    currentPage: number;
};

type Sort = {
    key: string;
    direction: string;
};

type InternalIdCell = {
    label: string;
};

type RowRecord = {
    internalId?: InternalIdCell;
    name?: string;
    [key: string]: unknown;
};

type ClientsTableProps = {
    columns?: Record<string, unknown>[];
    data?: RowRecord[];
    isLoading?: boolean;
    paging?: Paging;
    sort?: Sort;
    onChangeSort?: (value: Sort) => void;
    onChangeFilter?: (value: unknown) => void;
    onChangePagination?: (page: number) => void;
    tab?: string | null;
    selection?: 'radio' | 'checkbox' | null;
    handleRowClick?: ((record: RowRecord) => void) | null;
};

const ClientsTable = ({
    columns = [],
    data = [],
    isLoading = false,
    paging = {
        pageSize: 0,
        totalCount: 0,
        currentPage: 0,
    },
    sort = {
        key: '',
        direction: 'asc',
    },
    onChangeSort = () => null,
    onChangeFilter = () => null,
    onChangePagination = () => null,
    tab = '',
    selection = null,
    handleRowClick = null,
}: ClientsTableProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const onRowClick = (record: RowRecord) => ({
        onClick: () => {
            if (isLoading) {
                return;
            }

            if (handleRowClick) {
                handleRowClick(record);
            } else {
                navigate(`/client/${record.internalId?.label}/dashboard`, {
                    fromTab: tab || 'clients',
                    clientName: record.name,
                });
            }
        },
    });

    const rowSelection = selection ? { type: selection } : undefined;

    return (
        <>
            <Table
                noDataText={t('clients.noClients')}
                onChangeSort={onChangeSort}
                onChangeFilter={onChangeFilter}
                columns={columns}
                paging={{
                    pageSize: paging.pageSize,
                    hidePaging: true,
                }}
                data={data}
                sorting={sort}
                onRow={onRowClick}
                headerControls={{
                    selectControlsLabel: {
                        selectAll: t('headerControls.selectAll'),
                        reset: t('headerControls.reset'),
                    },
                }}
                rowSelection={rowSelection}
            />
            <ClientsPagination
                total={paging.totalCount}
                currentPage={paging.currentPage}
                pageSize={paging.pageSize}
                onChange={onChangePagination}
            />
        </>
    );
};

export default ClientsTable;
